import { RefObject, useMemo, lazy, useEffect, CSSProperties, useRef, useState } from 'react';
import type ReactQuill from 'react-quill-new';

import SSRQuillWrapper from './ssr-quill-wrapper.js';

const defaultModules = {};

interface Props {
    quillRef?: RefObject<ReactQuill>;
    value?: string;
    onChange?: (value: string, delta: any, source: string) => void;
    modules?: any;
    formats?: any;
    readOnly?: boolean;
    disableClicks?: boolean;
    onMouseMove?: (evt: any) => void;
    onMouseLeave?: (evt: any) => void;
    onClick?: (evt: any) => void;
    style?: CSSProperties;
}

const ReactQuillComponent = lazy(() => {
    if (typeof document !== 'undefined') {
        return import('react-quill-new');
    } else {
        return {
            __esModule: true,
            default: () => null,
        } as any;
    }
});

const QuillWrapper: FC<Props> = ({
    quillRef,
    value = '',
    readOnly,
    modules = {},
    disableClicks = false,
    onClick,
    onMouseLeave,
    onMouseMove,
    style,
    formats,
    onChange,
}) => {
    const [render, setRender] = useState(false);
    const _quillRef = useRef<ReactQuill>();
    const ref = quillRef ?? _quillRef;
    const processedValue = readOnly
        ? // Change play link class to indicate this is a read-only version of the link
          value?.replace(/<a class="play-link-pill" href="(.+?)">/g, '<a class="play-link-pill read-only" href="$1">')
        : value?.replace(/&nbsp;/g, ' ');

    useEffect(() => {
        setRender(true);
    }, []);

    const classNames = ['quill-wrapper'];

    if (readOnly) classNames.push('readonly');

    // If the modules or formats change on every render, the entire Quill editor will re-render and cause issues
    const totalModules = useMemo(() => {
        return {
            ...defaultModules,
            ...modules,
        };
    }, [modules]);

    const quill = render ? (
        <ReactQuillComponent
            key={readOnly ? processedValue : undefined}
            ref={ref}
            value={processedValue}
            readOnly={readOnly}
            modules={totalModules}
            formats={formats}
            onChange={onChange}
            useSemanticHTML={false}
        />
    ) : (
        <SSRQuillWrapper value={processedValue} />
    );

    return (
        <div
            className={classNames.join(' ')}
            onClick={readOnly && !disableClicks ? onClick : null}
            onMouseMove={onMouseMove}
            onMouseLeave={onMouseLeave}
            style={style}
        >
            {quill}
        </div>
    );
};

export default QuillWrapper;
